// =============================================================================
// src/features/analytics/analytics-summary-markup.ts - KPIs e graficos Analytics.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import {
  type OmarAnalyticsChartMode,
  type OmarAnalyticsKpi,
} from './analytics-data';
import { buildAnalyticsChartStageMarkup } from './analytics-chart-markup';
import { analyticsDemoTagMarkup } from './analytics-demo-tag';
import { OMAR_ANALYTICS_TREND_ICONS } from './analytics-labels';
import { analyticsLiveSource } from './analytics-live-store';

/** Renderiza a faixa de KPIs executivos. */
export function buildAnalyticsKpisMarkup(analyticsKpis: readonly OmarAnalyticsKpi[]): string {
  return `<div class="u-analytics-kpis">
    ${analyticsKpis.map(analyticsKpiMarkup).join('')}
  </div>`;
}

/** Renderiza o painel de volume e alcance compartilhado. */
export function buildAnalyticsChartPanelMarkup(panelId: string, panelTitle: string): string {
  // Live nao tem alcance nem sentimento (F3.1/F3.2): titulo, kicker e legenda
  // param de prometer series que o grafico nao desenha ("nao condiz com os
  // numeros", pedido 2026-07-25). Mock mantem o painel completo original.
  const isLive = analyticsLiveSource() === 'live';
  const effectiveTitle = isLive ? 'Mencoes por dia' : panelTitle;
  return `<section class="u-analytics-panel u-analytics-chart-panel" id="${panelId}" data-analytics-chart-panel="${panelId}" aria-labelledby="${panelId}Title">
    <div class="u-analytics-panel-head">${analyticsChartPanelTitleMarkup(panelId, effectiveTitle, isLive)}${analyticsChartToolsMarkup(panelId)}</div>
    ${buildAnalyticsChartStageMarkup('days', 'reach')}
    ${analyticsChartLegendMarkup(isLive)}
  </section>`;
}

function analyticsChartLegendMarkup(isLive: boolean): string {
  const liveEntries = '<span><i class="mentions"></i>Mencoes</span>';
  const mockEntries = `${liveEntries}<span><i class="reach"></i>Alcance</span><span><i class="positive"></i>Positivo</span><span><i class="negative"></i>Critico</span>`;
  return `<div class="u-analytics-chart-legend">${isLive ? liveEntries : mockEntries}</div>`;
}

function analyticsChartPanelTitleMarkup(panelId: string, panelTitle: string, isLive: boolean): string {
  const kicker = isLive ? 'Volume de mencoes' : 'Volume, alcance e sentimento';
  return `<div><span>${kicker}</span><h2 id="${panelId}Title">${escapeHtml(panelTitle)}</h2></div>`;
}

function analyticsChartToolsMarkup(panelId: string): string {
  return `<div class="u-analytics-chart-tools">${analyticsChartModeTabsMarkup(panelId)}${analyticsChartGranularityMarkup(panelId)}</div>`;
}

function analyticsChartModeTabsMarkup(panelId: string): string {
  // Live (2026-07-25, "remover os conteudos mockados"): sem serie de sentimento
  // na coleta, a tab demonstrativa SOME — so ha um modo, entao nao ha o que
  // alternar (o chart-controller cai em 'reach' sem botoes). Mock mantem as duas.
  if (analyticsLiveSource() === 'live') return '';
  return `<div class="u-analytics-chart-tabs" role="group" aria-label="Modo do grafico">
    ${analyticsChartModeButtonMarkup(panelId, 'reach', 'Mencoes & alcance', true)}
    ${analyticsChartModeButtonMarkup(panelId, 'sentiment', 'Sentimento', false)}
  </div>`;
}

function analyticsChartModeButtonMarkup(panelId: string, mode: OmarAnalyticsChartMode, label: string, active: boolean): string {
  const activeClass = active ? ' is-active' : '';
  // Tab de sentimento desabilitada quando live: a serie viva nao tem sentimento
  // (F3.1) e zeros desenhados leriam como dado real — opcao dura registrada no plano.
  const sentimentDisabled = mode === 'sentiment' && analyticsLiveSource() === 'live';
  const disabledAttrs = sentimentDisabled ? ' disabled title="Sentimento chega na proxima fase do monitoramento ao vivo"' : '';
  const demoTag = sentimentDisabled ? analyticsDemoTagMarkup() : '';
  return `<button type="button" class="${activeClass.trim()}" aria-pressed="${active ? 'true' : 'false'}" data-analytics-chart-target="${panelId}" data-analytics-chart-mode="${mode}"${disabledAttrs}>${label}${demoTag}</button>`;
}

function analyticsChartGranularityMarkup(panelId: string): string {
  return `<select class="u-analytics-chart-granularity" data-analytics-granularity="${panelId}" aria-label="Granularidade do grafico">
    <option value="days">Dias</option><option value="weeks">Semanas</option><option value="months">Meses</option>
  </select>`;
}

function analyticsKpiMarkup(analyticsKpi: OmarAnalyticsKpi): string {
  // Sem tag demonstracao: no live os 4 KPIs sao reais desde 2026-07-25
  // (views de video e municipios citados substituiram alcance/sentimento mock).
  return `<article class="u-analytics-kpi">
    <span>${escapeHtml(analyticsKpi.label)}</span><strong>${escapeHtml(analyticsKpi.value)}</strong>
    <em class="is-${analyticsKpi.trend}"><i class="ti ${OMAR_ANALYTICS_TREND_ICONS[analyticsKpi.trend]}" aria-hidden="true"></i>${escapeHtml(analyticsKpi.delta)}</em>
    <small>${escapeHtml(analyticsKpi.context)}</small>
  </article>`;
}
